import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const JobDetailModal = ({ job, onClose, onApply }) => {
  const [activeTab, setActiveTab] = useState('details');

  if (!job) return null;

  const getCategoryIcon = (category) => {
    const icons = {
      'Plumbing': 'Wrench',
      'Electrical': 'Zap',
      'Carpentry': 'Hammer',
      'Cleaning': 'Sparkles',
      'Babysitting': 'Baby',
      'Tutoring': 'GraduationCap',
      'Driving': 'Car',
      'Cooking': 'ChefHat',
      'Laundry': 'Shirt',
      'Errands': 'ShoppingBag',
      'Tech Support': 'Laptop',
      'Beauty Services': 'Scissors'
    };
    return icons?.[category] || 'Briefcase';
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-PH', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const tabs = [
    { id: 'details', label: 'Job Details', icon: 'FileText' },
    { id: 'client', label: 'Client Info', icon: 'User' },
    { id: 'requirements', label: 'Requirements', icon: 'CheckSquare' }
  ];

  return (
    <div
      className="fixed inset-0 z-modal bg-background/95 flex items-start justify-center pt-[8vh] px-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-card rounded-md shadow-warm-xl w-full max-w-[800px] mb-8 animate-in fade-in zoom-in-95 duration-250"
        onClick={(e) => e?.stopPropagation()}
      >
        <div className="sticky top-0 bg-card border-b border-border rounded-t-md z-10">
          <div className="flex items-center justify-between p-5 md:p-6">
            <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-primary/10 rounded-md flex items-center justify-center flex-shrink-0">
                <Icon name={getCategoryIcon(job?.category)} size={24} color="var(--color-primary)" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground line-clamp-1">
                  {job?.title}
                </h2>
                <p className="text-sm text-muted-foreground">{job?.category}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-md hover:bg-muted transition-smooth flex-shrink-0"
              aria-label="Close job details"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          <div className="flex border-t border-border overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`
                  flex items-center gap-2 px-4 md:px-6 py-3 text-sm md:text-base font-medium
                  transition-smooth border-b-2 whitespace-nowrap flex-shrink-0
                  ${activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }
                `}
              >
                <Icon name={tab?.icon} size={18} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="p-5 md:p-6">
          {activeTab === 'details' && (
            <div className="space-y-5 md:space-y-6">
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-secondary/10 text-secondary rounded-md text-sm font-medium">
                  {job?.category}
                </span>
                {job?.urgency !== 'normal' && (
                  <span className={`px-3 py-1 rounded-md text-sm font-medium ${
                    job?.urgency === 'urgent' ?'bg-error/10 text-error' :'bg-warning/10 text-warning'
                  }`}>
                    {job?.urgency === 'urgent' ? 'Urgent' : 'High Priority'}
                  </span>
                )}
                {job?.isVerified && (
                  <span className="px-3 py-1 bg-success/10 text-success rounded-md text-sm font-medium flex items-center gap-1">
                    <Icon name="Check" size={14} />
                    Verified Client
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-muted rounded-md">
                  <Icon name="Wallet" size={20} color="var(--color-primary)" />
                  <div>
                    <p className="text-xs text-muted-foreground">Budget Range</p>
                    <p className="text-base font-semibold text-foreground">
                      ₱{job?.budgetMin?.toLocaleString()} - ₱{job?.budgetMax?.toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-muted rounded-md">
                  <Icon name="MapPin" size={20} color="var(--color-primary)" />
                  <div>
                    <p className="text-xs text-muted-foreground">Location</p>
                    <p className="text-base font-semibold text-foreground line-clamp-1">{job?.location}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-muted rounded-md">
                  <Icon name="Calendar" size={20} color="var(--color-primary)" />
                  <div>
                    <p className="text-xs text-muted-foreground">Posted Date</p>
                    <p className="text-base font-semibold text-foreground">{formatDate(job?.postedDate)}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-muted rounded-md">
                  <Icon name="Users" size={20} color="var(--color-primary)" />
                  <div>
                    <p className="text-xs text-muted-foreground">Applicants</p>
                    <p className="text-base font-semibold text-foreground">{job?.applicantCount} applied</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-base font-heading font-semibold text-foreground mb-3">Description</h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed whitespace-pre-line">
                  {job?.description}
                </p>
              </div>

              <div>
                <h3 className="text-base font-heading font-semibold text-foreground mb-3">Required Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {job?.skills?.map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-primary/10 text-primary rounded-md text-sm font-medium"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'client' && (
            <div className="space-y-5 md:space-y-6">
              <div className="flex items-start gap-4 p-5 bg-muted rounded-md">
                <Image
                  src={job?.clientAvatar}
                  alt={job?.clientAvatarAlt}
                  className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-heading font-semibold text-foreground">
                      {job?.clientName}
                    </h3>
                    {job?.isVerified && (
                      <div className="w-5 h-5 bg-success rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name="Check" size={12} color="white" />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex items-center gap-1">
                      <Icon name="Star" size={16} color="var(--color-warning)" fill="var(--color-warning)" />
                      <span className="text-sm font-semibold text-foreground">{job?.clientRating}</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      ({job?.clientReviews} reviews)
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Member since {new Date(job.clientMemberSince)?.getFullYear()}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-muted rounded-md">
                  <p className="text-2xl font-bold text-primary mb-1">{job?.clientJobsPosted}</p>
                  <p className="text-xs text-muted-foreground">Jobs Posted</p>
                </div>
                <div className="text-center p-4 bg-muted rounded-md">
                  <p className="text-2xl font-bold text-success mb-1">{job?.clientHireRate}%</p>
                  <p className="text-xs text-muted-foreground">Hire Rate</p>
                </div>
                <div className="text-center p-4 bg-muted rounded-md col-span-2 md:col-span-1">
                  <p className="text-2xl font-bold text-foreground mb-1">₱{job?.clientAvgBudget?.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Avg. Budget</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'requirements' && (
            <div className="space-y-5 md:space-y-6">
              <div>
                <h3 className="text-base font-heading font-semibold text-foreground mb-3">
                  What You'll Need
                </h3>
                <ul className="space-y-3">
                  {job?.requirements?.map((req, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon name="Check" size={14} color="var(--color-success)" />
                      </div>
                      <span className="text-sm md:text-base text-foreground">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-4 bg-warning/10 border border-warning/20 rounded-md">
                <div className="flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-warning mb-1">Important Notes</h4>
                    <p className="text-sm text-muted-foreground">
                      Please ensure you meet all requirements before applying. The client may request verification of your skills and experience.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-5 md:p-6 rounded-b-md">
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              size="lg"
              fullWidth
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              variant="default"
              size="lg"
              fullWidth
              onClick={() => onApply(job)}
              iconName="Send"
              iconPosition="left"
            >
              Apply for This Job
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetailModal;